import { format } from 'date-fns';
import { memo, useCallback } from 'react';

import { Scroll, Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { FLOATING_TAB_BAR_CLEARANCE_TOKEN } from '@/components/ui/tabbar';
import { STRINGS } from '@/constants/strings';
import GuardianEventRow from '@/features/guardian/components/guardian-event-row';
import type { FamilyEvent } from '@/features/guardian/family-events';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

type FamilyDayPanelProps = {
  selectedDate: Date;
  events: FamilyEvent[];
  onEventPress?: (event: FamilyEvent) => void;
};

type FamilyDayPanelEventProps = {
  event: FamilyEvent;
  onPress?: (event: FamilyEvent) => void;
};

const FamilyDayPanelEvent = memo(({ event, onPress }: FamilyDayPanelEventProps) => {
  const handlePress = useCallback(() => onPress?.(event), [event, onPress]);

  return (
    <GuardianEventRow
      accessibilityLabel={`${event.startTime} · ${event.title} · ${event.bookingLabel}`}
      title={event.title}
      playerName={event.playerName}
      playerAvatar={event.playerAvatar}
      accentColor={event.accentColor}
      timeLabel={event.startTime}
      bookingLabel={event.bookingLabel}
      isBookedByCurrentGuardian={event.isBookedByCurrentGuardian}
      variant="dayPanel"
      onPress={onPress ? handlePress : undefined}
    />
  );
});

function FamilyDayPanel({ selectedDate, events, onEventPress }: FamilyDayPanelProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <Stack flex={1} minHeight={0} paddingBottom={scale(FLOATING_TAB_BAR_CLEARANCE_TOKEN)}>
      <Stack flex={1} minHeight={0} style={styles.panel}>
        <Stack paddingTop={scale(16)} paddingX={scale(16)} paddingBottom={scale(12)}>
          <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.weekday}>
            {format(selectedDate, 'EEEE')}
          </Text>
          <Text variant="Font Guardian App/Heading/Heading H6 18px" style={styles.date}>
            {format(selectedDate, 'MMMM d')}
          </Text>
        </Stack>

        <Scroll
          key={format(selectedDate, 'yyyy-MM-dd')}
          style={styles.scroll}
          nestedScrollEnabled
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}>
          {events.length > 0 ? (
            <Stack gap={scale(8)}>
              {events.map(event => (
                <FamilyDayPanelEvent key={event.id} event={event} onPress={onEventPress} />
              ))}
            </Stack>
          ) : (
            <Stack flex={1} align="center" paddingTop={scale(26)} gap={scale(12)}>
              <Stack
                width={scale(48)}
                height={scale(48)}
                borderRadius={scale(24)}
                align="center"
                justify="center"
                backgroundColor={guardianColors['1']}>
                <SvgIcon name="IcBook" size={scale(24)} color={guardianColors['6']} />
              </Stack>
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
                style={styles.emptyText}>
                {STRINGS.guardian.kids.noEventsScheduled}
              </Text>
            </Stack>
          )}
        </Scroll>
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  panel: {
    borderRadius: scale(16),
    borderCurve: 'continuous',
    overflow: 'hidden',
    backgroundColor: colors['0'],
  },
  scroll: {
    flex: 1,
    minHeight: 0,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: scale(16),
    paddingBottom: scale(16),
  },
  weekday: {
    color: guardianColors['6'],
  },
  date: {
    color: guardianColors['10'],
  },
  emptyText: {
    color: guardianColors['6'],
    textAlign: 'center',
  },
}));

export default memo(FamilyDayPanel);
